import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Settings, Save, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const SECTIONS = [
  {
    title: "Event Detection Thresholds",
    description: "When a driving event is flagged",
    fields: [
      { key: "speed_limit_mph",      label: "Speed limit (mph)", step: 1 },
      { key: "harsh_brake_drop_mph", label: "Harsh brake — speed drop (mph)", step: 0.5 },
      { key: "rapid_accel_rise_mph", label: "Rapid accel — speed rise (mph)", step: 0.5 },
      { key: "sharp_turn_deg",       label: "Sharp turn — heading change (deg)", step: 1 },
      { key: "sharp_turn_min_speed", label: "Sharp turn — minimum speed (mph)", step: 1 },
      { key: "event_window_s",       label: "Event window (seconds between samples)", step: 1 },
      { key: "idle_speed_mph",       label: "Idle speed threshold (mph)", step: 0.1 },
      { key: "extreme_speed_mph",    label: "Extreme speed alert (mph)", step: 1 },
    ],
  },
  {
    title: "Score Penalty Weights",
    description: "How much each event reduces the 100-pt safety score",
    fields: [
      { key: "w_speeding",      label: "Speeding sample penalty", step: 0.1 },
      { key: "w_harsh_brake",   label: "Harsh brake event penalty", step: 0.5 },
      { key: "w_rapid_accel",   label: "Rapid accel event penalty", step: 0.5 },
      { key: "w_sharp_turn",    label: "Sharp turn event penalty", step: 0.5 },
      { key: "w_extreme_speed", label: "Extreme speed event penalty", step: 0.5 },
    ],
  },
  {
    title: "Risk-Level Bands",
    description: "Minimum score for each band (Excellent / Good / Caution; below the lowest = Reckless)",
    fields: [
      { key: "risk_excellent_min", label: "Excellent threshold", step: 1 },
      { key: "risk_good_min",      label: "Good threshold",      step: 1 },
      { key: "risk_caution_min",   label: "Caution threshold",   step: 1 },
    ],
  },
];

export default function DrivingSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const canEdit = ["owner", "admin"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/management/driving-settings");
      setSettings(data.settings);
      setDefaults(data.defaults);
    } catch (e) { console.error(e); toast.error("Failed to load settings"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => {
    setSettings(s => ({ ...s, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await API.put("/management/driving-settings", settings);
      toast.success("Driving safety thresholds updated");
      load();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!defaults) return;
    setSaving(true);
    try {
      await API.put("/management/driving-settings", defaults);
      toast.success("Reset to defaults");
      load();
    } catch (e) {
      toast.error("Reset failed");
    } finally { setSaving(false); }
  };

  if (loading || !settings) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 text-zinc-700 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="driving-settings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-zinc-900 flex items-center gap-2">
            <Settings className="w-6 h-6 text-zinc-700" /> Driving Safety Rules
          </h1>
          <p className="text-sm text-zinc-500">
            Tune thresholds & live-alert behaviour. Changes apply instantly.
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={resetDefaults} disabled={saving}
              data-testid="reset-defaults-btn">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Reset Defaults
            </Button>
            <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      : <Save className="w-4 h-4 mr-1.5" />}
              Save Changes
            </Button>
          </div>
        )}
      </div>

      {!canEdit && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700"
          data-testid="readonly-banner">
          Read-only. Only Owner and Admin roles can edit thresholds.
        </div>
      )}

      {/* Live alerts toggle */}
      <Card className="border border-zinc-200">
        <CardContent className="p-5 flex items-center justify-between">
          <div>
            <p className="text-sm font-heading font-semibold text-zinc-900">Live Web-Push Alerts</p>
            <p className="text-xs text-zinc-500">
              When ON, management & branch managers receive a push notification within seconds of a flagged event.
            </p>
          </div>
          <Switch
            checked={!!settings.live_alerts_enabled}
            onCheckedChange={v => update("live_alerts_enabled", v)}
            disabled={!canEdit}
            data-testid="toggle-live-alerts"
          />
        </CardContent>
      </Card>

      {SECTIONS.map(section => (
        <Card key={section.title} className="border border-zinc-200">
          <CardContent className="p-5 space-y-4">
            <div>
              <h2 className="text-sm font-heading font-bold text-zinc-900">{section.title}</h2>
              <p className="text-xs text-zinc-500">{section.description}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {section.fields.map(f => (
                <div key={f.key}>
                  <Label className="text-[11px] text-zinc-600 font-bold uppercase tracking-wider">
                    {f.label}
                  </Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Input
                      type="number"
                      step={f.step}
                      value={settings[f.key]}
                      onChange={e => update(f.key, parseFloat(e.target.value) || 0)}
                      disabled={!canEdit}
                      data-testid={`field-${f.key}`}
                      className="text-sm"
                    />
                    <span className="text-[10px] text-zinc-400 whitespace-nowrap">
                      default {defaults?.[f.key]}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
